import cx from 'classnames';
import DetailsView from 'components/DetailsView';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { Button, PageHeader, SimpleLabel } from 'sema-ui-components';
import styles from './EnvironmentDetailsStyles.scss';
import { getJSON } from '../../../utils';
import { insertAggregatedStatus } from './EnvironmentsView';

export const OidcEnvironmentDetailsView = ({
  routes,
  params,
  setOverlay,
  unsetOverlay
}) => {
  const loc = 'identification_admin.environment.register_oidc';
  const displayName = data => (
    <div className={styles.displayName}>
      <h3>
        <FormattedMessage id="identification_admin.environment.details_display_name" />
      </h3>
      <div className="row">
        <div className="col-lg-8">
          <h5>
            <FormattedMessage id="identification_admin.environment.details_fi" />
          </h5>
        </div>
        <div className="col-lg-16">{data?.service_name_fi}</div>
      </div>
      <div className="row">
        <div className="col-lg-8">
          <h5>
            <FormattedMessage id="identification_admin.environment.details_sv" />
          </h5>
        </div>
        <div className="col-lg-16">{data?.service_name_sv}</div>
      </div>
      <div className="row">
        <div className="col-lg-8">
          <h5>
            <FormattedMessage id="identification_admin.environment.details_en" />
          </h5>
        </div>
        <div className="col-lg-16">{data?.service_name_en}</div>
      </div>
    </div>
  );

  const contactPerson = data => {
    if (!Array.isArray(data?.contact_person_emails)) {
      return null;
    }
    let roleLabel;
    return (
      <div>
        <h3>
          <FormattedMessage id="identification_admin.environment.details_contact_persons" />
        </h3>
        {data.contact_person_emails.map(email => (
          <div className="row">
            <div className="col-lg-8">
              <h5>
                <FormattedMessage id="identification_admin.environment.details_email_address" />
              </h5>
            </div>
            <div className="col-lg-16">{email}</div>
          </div>
        ))}
      </div>
    );
  };

  const statusIconLabel = {
    waitingForPublishing: {
      color: 'kanerva',
      abbreviation: (
        <FormattedMessage id="identification_admin.environment.wfp" />
      )
    },
    waitingForApproval: {
      color: 'taivas',
      abbreviation: (
        <FormattedMessage id="identification_admin.environment.wfa" />
      )
    },
    published: {
      color: 'havumetsa',
      abbreviation: <FormattedMessage id="identification_admin.environment.p" />
    },
    archived: {
      color: 'lakka',
      abbreviation: <FormattedMessage id="identification_admin.environment.a" />
    },
    released: {
      color: 'kanerva',
      abbreviation: (
        <FormattedMessage id="identification_admin.environment.wfp" />
      )
    }
  };

  const statusLabel = statusKey => {
    statusKey = statusKey === 'released' ? 'waitingForPublishing' : statusKey;
    return (
      <span>
        <SimpleLabel
          className={styles.label}
          labelString={statusIconLabel[statusKey].abbreviation}
          color={statusIconLabel[statusKey].color}
        />
        <FormattedMessage
          id={`identification_admin.environment.details_status_${statusKey}`}
        />
      </span>
    );
  };

  const [getData, setData] = useState(null);
  const [getOidcMetadata, setOidcMetadata] = useState(null);

  // NOTE: identification admin redux state data is used to construct data in other views.
  //       This is the same pattern as in EnvironmentDetailsView.js
  //       The unused data from this query shouldn't be redundantly stored in redux state.
  //       There is no real upside in implementing single metadata storing locally, ergo doing it inline.
  useEffect(() => {
    getJSON(`/api/identification-admin/environments/oidc/${params.env}`).then(
      data => {
        setData(data.data);
        setOidcMetadata(data.data);
      }
    );
  }, []);

  const targetEnvironmentLabel =
    getData?.targetEnv === 'production' ? (
      <FormattedMessage
        id={'identification_admin.environment.details_target_production'}
      />
    ) : (
      <FormattedMessage
        id={'identification_admin.environment.details_target_customer_test'}
      />
    );
  let aggregateStatus = null;
  if (getData?.publishingStatus?.name != undefined) {
    const sts = {
      status: { name: 'readyForApproval' }
    };
    aggregateStatus = statusLabel(insertAggregatedStatus(sts).aggregatedStatus);
  }

  // Stores the javascript object into a blob and makes it accessible to the browser
  const downloadMetadata = () => {
    const jsonString = JSON.stringify(getOidcMetadata, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'metadata.json';
    link.click();

    // Cleanup
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <PageHeader
        title={getData?.[`service_name_${params.lang.toLowerCase()}`]}
        routes={routes}
        params={params}
      />
      <DetailsView
        className={styles.detailsView}
        locale={params.lang}
        setOverlay={setOverlay}
        unsetOverlay={unsetOverlay}
        aside={
          <div>
            <div className="row">
              <h4>
                <FormattedMessage id="identification_admin.environment.column_state" />
              </h4>
            </div>
            <div className="row">{aggregateStatus}</div>
            <div className="row">
              <h5>
                <FormattedMessage id="identification_admin.environment.details_target_environment" />
              </h5>
            </div>
            <div className="row">{targetEnvironmentLabel}</div>
            <div className="row">
              <h4>
                <FormattedMessage id={`${loc}.type`} />
              </h4>
            </div>
            <div className="row">{getData?.type}</div>
            <div className={styles.buttonDiv}>
              <Button
                small
                onClick={async () => {
                  downloadMetadata();
                }}
              >
                <FormattedMessage id="identification_admin.environment.download_metadata" />
              </Button>
            </div>
          </div>
        }
        content={
          <div className={cx(styles.content, 'col-lg-24')}>
            {displayName(getData)}
            {contactPerson(getData)}
          </div>
        }
      />
    </div>
  );
};

OidcEnvironmentDetailsView.propTypes = {
  // Redux props
  // Routing
  currentEnvironment: PropTypes.object,
  currentEnvironmentContacts: PropTypes.array,
  //  isFetching: PropTypes.boolean,
  params: PropTypes.object,
  routes: PropTypes.array,
  // actionCreators
  setOverlay: PropTypes.func,
  unsetOverlay: PropTypes.func
};

export default OidcEnvironmentDetailsView;
